import { supabase } from '../config/supabase';

interface Section {
  title: string;
  imageUrl: string;
  content: string;
  trainingUrl: string;
}

export interface MenuItem {
  id: string;
  label: string;
  url: string;
  target: string;
}

export interface FooterSettings {
  address: string;
  phone: string;
  mobile: string;
  email: string;
}

export interface ImageSettings {
  url: string;
  opacity: number;
}

export interface ThemeSettings {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  images: {
    logo?: ImageSettings;
    sidebarBackground?: ImageSettings;
    mainBackground?: ImageSettings;
    loginBackground?: ImageSettings;
  };
}

export interface LandingContent {
  title: string;
  subtitle: string;
  backgroundImage: string;
  bannerImages: [string, string, string, string];
  menuItems: MenuItem[];
  footer: FooterSettings;
  section1: Section;
  section2: Section;
  section3: Section;
  section4: Section;
  useFormasaasStyle: boolean;
  visibleSections: {
    menu: boolean;
    header: boolean;
    footer: boolean;
    backgroundImage: boolean;
    banner: boolean;
    section1: boolean;
    section2: boolean;
    section3: boolean;
    section4: boolean;
    search: boolean;
  };
}

const defaultTheme: ThemeSettings = {
  primaryColor: '#0066b3',
  secondaryColor: '#4b5563',
  backgroundColor: '#f9fafb',
  textColor: '#111827',
  images: {}
};

const defaultContent: LandingContent = {
  title: 'Bienvenue chez CQO',
  subtitle: 'Retrouvez ci-dessous nos formations dédiées à nos applications',
  backgroundImage: '',
  bannerImages: [
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1170&h=450&fit=crop'
  ],
  menuItems: [
    {
      id: '1',
      label: 'A propos de CQO',
      url: 'https://www.bycqo.com',
      target: '_blank'
    },
    {
      id: '2',
      label: 'Contactez nous',
      url: 'https://www.bycqo.com/contact',
      target: '_blank'
    }
  ],
  footer: {
    address: '12 rue du 35ème régiment d\'aviation - 69500, Bron',
    phone: '+33 (0)4 27 11 85 26',
    mobile: '06 08 61 40 91',
    email: 'formalog@cqo-dentaire.fr'
  },
  section1: {
    title: 'Section 1',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  section2: {
    title: 'Section 2',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  section3: {
    title: 'Section 3',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  section4: {
    title: 'Section 4',
    imageUrl: '',
    content: '',
    trainingUrl: ''
  },
  useFormasaasStyle: false,
  visibleSections: {
    menu: true,
    header: true,
    footer: true,
    backgroundImage: true,
    banner: true,
    section1: true,
    section2: true,
    section3: true,
    section4: true,
    search: true
  }
};

export const settingsService = {
  async getLandingContent(): Promise<LandingContent> {
    try {
      const { data, error } = await supabase
        .from('settings_landing')
        .select('*')
        .eq('id', 'main')
        .single();

      console.log('Raw data from settings_landing:', data);
      console.log('visible_sections from DB:', data?.visible_sections);

      if (error || !data) {
        console.log('No data found, using default content');
        await this.saveLandingContent(defaultContent);
        return defaultContent;
      }

      // Ensure we always have exactly 4 banner images
      const bannerImages = [
        ...(data.banner_images || []),
        ...defaultContent.bannerImages
      ].slice(0, 4) as [string, string, string, string];

      const sections = data.sections || {};

      return {
        title: data.title || defaultContent.title,
        subtitle: data.subtitle || defaultContent.subtitle,
        backgroundImage: data.background_image || defaultContent.backgroundImage,
        bannerImages,
        menuItems: data.menu_items || defaultContent.menuItems,
        footer: {
          address: data.footer?.address || defaultContent.footer.address,
          phone: data.footer?.phone || defaultContent.footer.phone,
          mobile: data.footer?.mobile || defaultContent.footer.mobile,
          email: data.footer?.email || defaultContent.footer.email
        },
        section1: {
          title: sections.section1?.title || defaultContent.section1.title,
          imageUrl: sections.section1?.imageUrl || defaultContent.section1.imageUrl,
          content: sections.section1?.content || defaultContent.section1.content,
          trainingUrl: sections.section1?.trainingUrl || defaultContent.section1.trainingUrl
        },
        section2: {
          title: sections.section2?.title || defaultContent.section2.title,
          imageUrl: sections.section2?.imageUrl || defaultContent.section2.imageUrl,
          content: sections.section2?.content || defaultContent.section2.content,
          trainingUrl: sections.section2?.trainingUrl || defaultContent.section2.trainingUrl
        },
        section3: {
          title: sections.section3?.title || defaultContent.section3.title,
          imageUrl: sections.section3?.imageUrl || defaultContent.section3.imageUrl,
          content: sections.section3?.content || defaultContent.section3.content,
          trainingUrl: sections.section3?.trainingUrl || defaultContent.section3.trainingUrl
        },
        section4: {
          title: sections.section4?.title || defaultContent.section4.title,
          imageUrl: sections.section4?.imageUrl || defaultContent.section4.imageUrl,
          content: sections.section4?.content || defaultContent.section4.content,
          trainingUrl: sections.section4?.trainingUrl || defaultContent.section4.trainingUrl
        },
        useFormasaasStyle: data.use_formasaas_style ?? defaultContent.useFormasaasStyle,
        visibleSections: {
          menu: data.visible_sections?.menu ?? defaultContent.visibleSections.menu,
          header: data.visible_sections?.header ?? defaultContent.visibleSections.header,
          footer: data.visible_sections?.footer ?? defaultContent.visibleSections.footer,
          backgroundImage: data.visible_sections?.backgroundImage ?? defaultContent.visibleSections.backgroundImage,
          banner: data.visible_sections?.banner ?? defaultContent.visibleSections.banner,
          section1: data.visible_sections?.section1 ?? defaultContent.visibleSections.section1,
          section2: data.visible_sections?.section2 ?? defaultContent.visibleSections.section2,
          section3: data.visible_sections?.section3 ?? defaultContent.visibleSections.section3,
          section4: data.visible_sections?.section4 ?? defaultContent.visibleSections.section4,
          search: data.visible_sections?.search ?? defaultContent.visibleSections.search
        }
      };
    } catch (error) {
      console.error('Error fetching landing content:', error);
      return defaultContent;
    }
  },

  async saveLandingContent(content: LandingContent): Promise<void> {
    const dataToSave = {
      id: 'main',
      title: content.title,
      subtitle: content.subtitle,
      background_image: content.backgroundImage,
      banner_images: content.bannerImages,
      menu_items: content.menuItems,
      footer: content.footer,
      sections: {
        section1: content.section1,
        section2: content.section2,
        section3: content.section3,
        section4: content.section4
      },
      use_formasaas_style: content.useFormasaasStyle,
      visible_sections: content.visibleSections
    };

    console.log('Saving to settings_landing:', dataToSave);

    const { error } = await supabase.from('settings_landing').upsert(dataToSave);

    if (error) {
      console.error('Error saving landing content:', error);
      throw new Error(`Impossible de sauvegarder: ${error.message} (Code: ${error.code})`);
    }
  },

  async getTheme(): Promise<ThemeSettings> {
    try {
      const { data, error } = await supabase
        .from('settings_theme')
        .select('*')
        .eq('id', 'main')
        .single();

      if (error || !data) {
        await this.saveTheme(defaultTheme);
        return defaultTheme;
      }

      return {
        primaryColor: data.primary_color || defaultTheme.primaryColor,
        secondaryColor: data.secondary_color || defaultTheme.secondaryColor,
        backgroundColor: data.background_color || defaultTheme.backgroundColor,
        textColor: data.text_color || defaultTheme.textColor,
        images: data.images || {}
      };
    } catch (error) {
      console.error('Error fetching theme:', error);
      return defaultTheme;
    }
  },

  async saveTheme(theme: ThemeSettings): Promise<void> {
    const { error } = await supabase.from('settings_theme').upsert({
      id: 'main',
      primary_color: theme.primaryColor,
      secondary_color: theme.secondaryColor,
      background_color: theme.backgroundColor,
      text_color: theme.textColor,
      images: theme.images
    });

    if (error) {
      console.error('Error saving theme:', error);
      throw new Error('Impossible de sauvegarder le thème');
    }
  },

  async uploadImage(file: File, type: keyof ThemeSettings['images']): Promise<string> {
    const fileName = `${type}/${Date.now()}_${file.name}`;

    const { error: uploadError } = await supabase.storage
      .from('themes')
      .upload(fileName, file);

    if (uploadError) {
      console.error('Error uploading image:', uploadError);
      throw new Error('Impossible de télécharger l\'image');
    }

    const { data } = supabase.storage
      .from('themes')
      .getPublicUrl(fileName);

    return data.publicUrl;
  }
};
